import { PageHeader, Text } from "#app/components/text.tsx"
import { DocumentCard } from "#app/components/documen-list-section.tsx"
import { useState } from "react"
import Cta from "#app/components/cta.tsx"
import { Link } from "react-router"
import { buttonVariants } from "#app/components/ui/button.tsx"
import { Icon } from "#app/components/ui/icon.tsx"
import { FILTERED_SERVICES_QUERY, FilteredServices, Services, SERVICES_QUERY } from "#app/queries/services.ts"
import { Route } from "./+types"
import { COMPANY_NAME, COMPANY_PHONE, SERVICE_REGION } from "#app/utils/constants.ts"
import { getSeoMeta } from "#app/utils/seo/getSeoMeta.ts"
import { BASE_URL } from "#app/utils/constants.ts"
import { SEOHandle } from "@nasa-gcn/remix-seo"
import { serverOnly$ } from "vite-env-only/macros";
import { formatPhoneNumberForTelLink } from "#app/utils/misc.tsx"


export const meta: Route.MetaFunction = ({ data }) => {
    const filter = data?.filter || 'all'; // Default to 'all' if filter is missing

    let title: string;
    let description: string;
    let url: string = BASE_URL + '/services';

    switch (filter) {
        case 'residential':
            title = `Residential Garage Door Services | ${SERVICE_REGION} | ${COMPANY_NAME}`;
            description = `Expert residential garage door installation, repair, and maintenance services offered by ${COMPANY_NAME} in ${SERVICE_REGION}. Find solutions for your home.`;
            break;
        case 'commercial':
            title = `Commercial Garage Door Services | ${SERVICE_REGION} | ${COMPANY_NAME}`;
            description = `Professional commercial overhead door services including repair, installation, and maintenance in ${SERVICE_REGION}. ${COMPANY_NAME} keeps your business running.`;
            break;
        case 'all':
        default:
            title = `Garage Door Repair & Installation Services | ${SERVICE_REGION} | ${COMPANY_NAME}`;
            description = `View all residential and commercial garage door services offered by ${COMPANY_NAME} in ${SERVICE_REGION}. Expert repairs, installation, and maintenance.`;
            break;
    }
    return getSeoMeta({
        title,
        description,
        url, 
    })	
}

const serverHandle: SEOHandle = {
  getSitemapEntries: async (request) => {
    const { getServicesSitemapEntries } = await import('#app/utils/seo/sitemap-utils.server.ts');
    return getServicesSitemapEntries(request);
  },
};

export const handle = serverOnly$(serverHandle);

export const loader = async ({ request }: Route.LoaderArgs) => {
    const { client } = await import('#app/utils/sanity-client.ts');
    const searchParams = new URL(request.url).searchParams;
    const filter = searchParams.get('filter');
    const query = filter ? FILTERED_SERVICES_QUERY.query : SERVICES_QUERY.query
    const params = {};
    if (filter) {
        Object.assign(params, { audience: filter })
    }
    const services = await client.fetch<Services | FilteredServices>(query, params)

    if (!services) {
        throw new Response('Not found', { status: 404 })
    }
    return {
        services,
        filter
    }
}


export default function ServicesPage({loaderData}: Route.ComponentProps) {
    const { services } = loaderData;
    const [hovered, setHovered] = useState<number | null>(null);
    let heading = "Garage Door Repair & Installation Services";
    let description = `View all residential and commercial garage door services offered by ${COMPANY_NAME} in ${SERVICE_REGION}. Expert repairs, installation, and maintenance.`;
    if (loaderData.filter === 'residential') {
        heading = "Residential Garage Door Services";
        description = `Expert residential garage door installation, repair, and maintenance services offered by ${COMPANY_NAME} in ${SERVICE_REGION}. Find solutions for your home.`;
    } else if (loaderData.filter === 'commercial') {
        heading = "Commercial Garage Door Services";
        description = `Professional commercial overhead door services including repair, installation, and maintenance in ${SERVICE_REGION}. ${COMPANY_NAME} keeps your business running.`;
    }
    const phoneLink = formatPhoneNumberForTelLink(COMPANY_PHONE);
    return (
        <main>
            <PageHeader variant="servicesPage" heading={heading} className="bg-primary text-primary-foreground text-center">
                <Text size="lg">
                    {description}
                </Text>
            </PageHeader>
            {/* Section to display the service cards */}
            <section className="bg-background py-16 sm:py-24"> {/* Example background/padding */}
                <div className="container">
                    {services && services.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10"> {/* Use grid layout */}
                            {services.map((service, index) => (
                                <DocumentCard
                                    // Key should be stable and unique, _id is best
                                    key={service._id}
                                    // Ensure the 'service' object matches the expected 'document' prop type
                                    // You might need to adjust zDocumentListItem if RESIDENTIAL_SERVICES_QUERY returns a slightly different shape
                                    document={service as any} // Cast or ensure type match
                                    index={index}
                                    hovered={hovered}
                                    setHovered={setHovered}
                                />
                            ))}
                        </div>
                    ) : (
                         <p className="text-center text-lg">No residential services found at this time. Please check back later.</p> // Message if no services
                    )}
                </div>
            </section>
            <Cta heading="Need a Repair or Installation Quote?" backgroundStyle="accent">
                <Link to="/get-a-quote" className={buttonVariants({ size: "xl" })}>
                    Get a free quote
                    <Icon name="dollar-sign" />
                </Link>
                {phoneLink && (
                <Link to={phoneLink} className={buttonVariants({ variant: "white", size: "xl" })}>
                    {COMPANY_PHONE}
                    <Icon name="phone-outgoing" />
                </Link> 
                )}
            </Cta>
        </main>
    )
}